/**
 * The scope picker (BON-534, generalised in BON-538).
 *
 * Worn beside the page title, matching the design's `page-header.jsx`, which
 * pairs a fixed `<h1>` with the region selector rather than making the region
 * name the heading.
 *
 * Two presentations, as the design has: a dropdown anchored to the trigger on
 * pointer-sized screens, and a bottom SHEET on a phone. The dropdown is not
 * merely cramped at 390px — anchored near the left edge it runs off the right,
 * clipping the director and the region count. The rows are shared between the
 * two, so only the chrome differs.
 *
 * The phone trigger is a tappable pill rather than plain text with a caret: at
 * this size it has to read as a control, and it needs a real tap target.
 *
 * It ranges over the whole hierarchy because every account reads every scope —
 * there is no per-account filtering here.
 *
 * The regions and the hrefs are the caller's: Overview reads them from its
 * all-regions query and addresses a region by ancestor path, Venues from the
 * region hierarchy and addresses it by query parameter. Fetching either here,
 * or knowing how either spells a URL, is what would tie the control back to
 * one page — so which region is CURRENT is an id, not an address.
 */
import { useState } from 'react';

import type { ScopeRowVariant } from './scope-row-metrics';
import type { ScopeHrefFor, ScopeRegion } from './scope.types';
import { ScopeMenu } from './ScopeMenu';
import { ScopeRows, type OwnRegionsRow } from './ScopeRows';
import { ScopeSheet } from './ScopeSheet';
import { ScopeTrigger } from './ScopeTrigger';

export interface ScopePickerProps {
  /** What the current scope is called. */
  label: string;
  /** The region currently being viewed; null at all-regions scope. */
  currentRegionId: string | null;
  /** The whole hierarchy, flat, parent before child. */
  regions: ScopeRegion[];
  hrefFor: ScopeHrefFor;
  /** The caller owns the breakpoint: Overview treats anything under 1024 as a
   *  phone because of its calendar, which is not a rule other pages share. */
  isPhone: boolean;
  /** False where the all-regions scope is not this viewer's to read. */
  showAllRegions?: boolean;
  /** A "Your regions" row, for a viewer assigned several. */
  ownRegions?: OwnRegionsRow;
}

export function ScopePicker({
  label,
  currentRegionId,
  regions,
  hrefFor,
  isPhone,
  showAllRegions = true,
  ownRegions,
}: ScopePickerProps) {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  /** The rows, built once for both presentations — only their sizing differs,
   *  and building them twice is what let the two drift apart. */
  const rows = (variant: ScopeRowVariant) => (
    <ScopeRows
      regions={regions}
      currentRegionId={currentRegionId}
      hrefFor={hrefFor}
      onPick={close}
      variant={variant}
      showAllRegions={showAllRegions}
      ownRegions={ownRegions}
    />
  );

  return (
    <div
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        maxWidth: '100%',
      }}
    >
      <ScopeTrigger
        label={label}
        open={open}
        onToggle={() => setOpen((wasOpen) => !wasOpen)}
        isPhone={isPhone}
      />

      {open &&
        (isPhone ? (
          <ScopeSheet title="Region" onClose={close}>
            {rows('sheet')}
          </ScopeSheet>
        ) : (
          <>
            {/* A full-viewport scrim under the panel, as the design does it: any
                click that isn't on the menu lands here and dismisses. Cheaper
                and more reliable than a document listener, which has to
                special-case the click that opened the panel. */}
            <div
              data-testid="scope-scrim"
              onClick={close}
              style={{ position: 'fixed', inset: 0, zIndex: 55 }}
            />
            <ScopeMenu>{rows('menu')}</ScopeMenu>
          </>
        ))}
    </div>
  );
}
